import type { SgBootstrapTheme, SgBootstrapThemeExtended } from '@sgwt/sgwt-widgets-react';
export const themeKey = 'Theme';

declare global {
  interface Window {
    SGWTWidgetConfiguration: {
      context?: {
        getSgDesignSystemTheme(): SgBootstrapTheme;
      };
    };
  }
}

const sgbsVersion = import.meta.env.VITE_SGBS_VERSION;
const cdnUrl = `https://sgwt-cdn-sgbs.sgmarkets.com/sg-bootstrap/${sgbsVersion}`;

function sgBoostrapUrls(variation: SgBootstrapTheme) {
  return {
    sgbs: `${cdnUrl}/core/css/sg-bootstrap-${variation}.min.css`,
    // aggrid: `https://sgwt-cdn-sgbs.sgmarkets.com/sg-bootstrap/5.29.0/ag-grid-theme/31-${variation}.min.css`,
    aggridCondensed: `https://sgwt-cdn-sgbs.sgmarkets.com/sg-bootstrap/5.29.0/ag-grid-theme/31-${variation}-condensed.min.css`,
    icons: `${cdnUrl}/icons/index.min.css`,
  };
}

export function initSgBootstrap() {
  const theme = window.SGWTWidgetConfiguration.context?.getSgDesignSystemTheme();
  updateTheme(theme ?? 'system');
}

export function getTheme(): SgBootstrapTheme {
  const savedTheme = getFromLocalStorage();
  if (savedTheme !== null) {
    return savedTheme?.toUpperCase() === 'DARK' ? 'dark' : 'standard';
  }
  const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  return prefersDark ? 'dark' : 'standard';
}

function saveToLocalStorage(theme: SgBootstrapThemeExtended) {
  if (typeof localStorage === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(themeKey, theme);
  } catch {
    console.error('Unable to store theme, localStorage might be full');
  }
}

function getFromLocalStorage(): string | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  return localStorage.getItem(themeKey);
}

export function updateTheme(theme: SgBootstrapThemeExtended): void {
  updateThemeUrls(getVariation(theme));
  saveToLocalStorage(getVariation(theme));
}

export function getVariation(theme: SgBootstrapThemeExtended | undefined): SgBootstrapTheme {
  switch (theme) {
    case 'standard':
    case 'dark':
      return theme;
    default: {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      return prefersDark ? 'dark' : 'standard';
    }
  }
}

function updateThemeUrls(theme: SgBootstrapTheme): void {
  for (const [id, url] of Object.entries(sgBoostrapUrls(theme))) {
    appendUniqueLinkToDocument(id, url);
  }
}

function appendUniqueLinkToDocument(id: string, url: string) {
  const linkElement = document.querySelector<HTMLLinkElement>(`link[id="${id}"]`);
  const newLinkElement = document.createElement('link');
  newLinkElement.rel = 'stylesheet';
  newLinkElement.id = id;
  newLinkElement.href = url;
  if (linkElement !== null) {
    newLinkElement.onload = () => {
      document.head.removeChild(linkElement);
    };
  }
  document.head.appendChild(newLinkElement);
}
